/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import { Option } from 'react-dropdown';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Text } from '#atoms';
import { TIME_LIST } from '#const/date';
import queryKeys from '#hooks/queries/queryKeys';
import { usePatchPersonalScheduleMutaion } from '#hooks/queries/schedules/usePatchPersonalScheduleMutaion';
import { usePostPersonalScheduleMutaion } from '#hooks/queries/schedules/usePostPersonalScheduleMutaion';
import { useScheduleCoachPersonalDetailQuery } from '#hooks/queries/schedules/useScheduleCoachPersonalDetailQuery';
import { LabelWithButton } from '#molecules';
import { useToast } from '#providers/ToastProvider';
import { FibudCalendarV2 } from 'src/components/molecules/FibudCalendarV2';
import ChipButtons from 'src/components/shared/ChipButtons';
import { getTime } from '../utils';
import { EVENT_SCHEDULE_CATEGORY_LIST } from './constants';
import * as S from './styles';
import TagManager from 'react-gtm-module';

dayjs.locale('ko');

export interface PersonalScheduleHandle {
  handleSave: () => void;
}

const PersonalScheduleContents = forwardRef<PersonalScheduleHandle>(
  (props, ref) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [searchParams] = useSearchParams();
    const personalScheduleId = decodeURIComponent(
      searchParams.get('personal-schedule-id') ?? ''
    );
    const isModify = !!personalScheduleId;
    const defaultDate = decodeURIComponent(
      searchParams.get('date') ??
        dayjs().set('hour', 6).set('minute', 0).format('YYYY-MM-DD HH:mm')
    );
    const { data: personalDetail, isLoading } =
      useScheduleCoachPersonalDetailQuery({
        personalScheduleId
      });

    const [title, setTitle] = useState<string>();
    const [activeDate, setActiveDate] = useState<dayjs.Dayjs>(
      defaultDate ? dayjs(defaultDate) : dayjs()
    );
    const [startTime, setStartTime] = useState<string>();
    const [endedTime, setEndedTime] = useState<string>();
    const [isCalenderOpen, setIsCalenderOpen] = useState<boolean>();
    const startTimeOptions = useMemo(() => {
      return [...TIME_LIST].slice(0, -1);
    }, []);

    const endTimeOptions = useMemo(() => {
      const [startHour, startMin] = getTime(startTime);
      const [endHour, endedMin] = getTime(endedTime);

      const index = TIME_LIST.findIndex(time => time === startTime);
      const options = [...TIME_LIST].splice(index + 1);

      if (
        startHour > endHour ||
        (startHour === endHour && startMin >= endedMin)
      ) {
        setEndedTime(options[0]);
      }

      return options;
    }, [startTime]);

    const { mutateAsync: postPersonalSchedule } =
      usePostPersonalScheduleMutaion();

    const { mutateAsync: patchPersonalSchedule } =
      usePatchPersonalScheduleMutaion();

    const handleChangeDate = (date: dayjs.Dayjs) => {
      setActiveDate(date);
      setIsCalenderOpen(false);
    };

    const handleStartTime = (timeDate: Option) => {
      setStartTime(timeDate.value);
    };

    const handleSave = async () => {
      if (!title) {
        showToast({
          type: 'fail',
          message: `제목을 입력해주세요.`
        });
        return;
      }

      if (!activeDate) {
        showToast({
          type: 'fail',
          message: `날짜를 선택해주세요.`
        });
        return;
      }

      if (!startTime || !endedTime) {
        showToast({
          type: 'fail',
          message: `시작 시간을 선택해주세요.`
        });
        return;
      }

      try {
        const [startHour, startMin] = getTime(startTime);
        const [endHour, endMin] = getTime(endedTime);
        const startDate = dayjs(activeDate)
          .set('hour', startHour)
          .set('minute', startMin);

        const endDate = dayjs(activeDate)
          .set('hour', endHour)
          .set('minute', endMin);

        const payload = {
          title,
          startAt: `${startDate.format('YYYY-MM-DD HH:mm')}`,
          endAt: `${endDate.format('YYYY-MM-DD HH:mm')}`
        };

        if (isModify) {
          await patchPersonalSchedule({
            id: personalScheduleId,
            ...payload
          });

          await queryClient.refetchQueries({
            queryKey: [queryKeys.PERSONAL_SCHEDULE, personalScheduleId]
          });
        } else {
          const plan_id = await postPersonalSchedule(payload); // 개인 일정
          TagManager.dataLayer({
            dataLayer: {
              event: 'calendertab_plan_saved',
              category: '일정 관리',
              action: 'calendertab_plan_saved',
              plan_id: plan_id,
              value: 1
            }
          });
          await queryClient.refetchQueries({
            queryKey: [queryKeys.PERSONAL_SCHEDULE]
          });
        }

        showToast({
          type: 'success',
          message: `일정 ${isModify ? '수정' : '등록'} 성공했습니다.`
        });

        navigate(-1);
      } catch (e: any) {
        showToast({
          type: 'fail',
          message: e?.response?.data?.message ?? '일정 등록 실패했습니다'
        });
      }
    };

    useEffect(() => {
      if (defaultDate) {
        const startTime = dayjs(defaultDate).format('HH:mm');
        const endedTime = dayjs(defaultDate).add(1, 'hour').format('HH:mm');

        setActiveDate(dayjs(defaultDate));
        setStartTime(startTime);
        setEndedTime(endedTime);
      }
    }, [defaultDate]);

    useImperativeHandle(ref, () => ({
      handleSave
    }));

    useEffect(() => {
      if (personalDetail) {
        setTitle(personalDetail.title);
        setActiveDate(dayjs(personalDetail.startAt));
        setStartTime(dayjs(personalDetail.startAt).format('HH:mm'));
        setEndedTime(dayjs(personalDetail.endAt).format('HH:mm'));
      }
    }, []);

    return (
      <S.Wrapper>
        <S.FormGroup>
          <S.Label>제목</S.Label>
          <S.Input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="제목을 입력해주세요."
          />
        </S.FormGroup>
        <S.ChipButtonsWrapper>
          <ChipButtons
            selectedValue={title}
            options={EVENT_SCHEDULE_CATEGORY_LIST}
            onChange={value => {
              setTitle(value);
            }}
          />
        </S.ChipButtonsWrapper>
        <S.StyledLine />
        <S.Group>
          <LabelWithButton
            active={!!isCalenderOpen}
            label="날짜"
            defaultButtonText={dayjs(activeDate).format('MM월DD일 (ddd)')}
            onClick={() => setIsCalenderOpen(prev => !prev)}
          />
          {isCalenderOpen && (
            <FibudCalendarV2
              defaultValue={dayjs(activeDate)}
              onChange={handleChangeDate}
            />
          )}
          <S.CountBox>
            <Text>시작</Text>
            <S.DropdownBox>
              <S.Dropdown
                value={startTime}
                placeholder="시작시간"
                options={startTimeOptions}
                onChange={handleStartTime}
                arrowClosed={undefined}
                arrowOpen={undefined}
              />
            </S.DropdownBox>
          </S.CountBox>

          <S.CountBox>
            <Text>종료</Text>
            <S.DropdownBox>
              <S.Dropdown
                value={endedTime}
                placeholder="종료시간"
                options={endTimeOptions}
                disabled={!startTime}
                arrowClosed={undefined}
                arrowOpen={undefined}
                onChange={option => setEndedTime(option.value)}
              />
            </S.DropdownBox>
          </S.CountBox>
        </S.Group>
      </S.Wrapper>
    );
  }
);

PersonalScheduleContents.displayName = 'PersonalScheduleContents';

export default PersonalScheduleContents;
